import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MapPin, Mail, Facebook, Instagram, ChevronRight, Target, Crosshair, Users, Trophy, Calendar, Shirt, Award, Star, Camera, Quote, ChevronDown, Send, CheckCircle, Phone, HelpCircle, Shield, CloudRain, DollarSign, FileText, Backpack } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get the correct photo URL
const getPhotoUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

// Camp photos from actual Burns Elite camps
const CAMP_PHOTOS = [
  {
    url: "/images/6lk4y9n2_IMG_4771.jpeg",
    alt: "Burns Elite camp group photo",
    caption: "Summer Camp 2024"
  },
  {
    url: "/images/z3olpt68_IMG_4769.jpeg",
    alt: "Young players with bats at camp",
    caption: "Batting Practice"
  },
  {
    url: "/images/50jt1kn4_IMG_4761.jpeg",
    alt: "Batting cage training session",
    caption: "Cage Work"
  },
  {
    url: "/images/64evyr35_IMG_4750.jpeg",
    alt: "Field training drills",
    caption: "Field Drills"
  },
  {
    url: "/images/0vg929mq_IMG_4741.png",
    alt: "Outfield training",
    caption: "Outfield Work"
  },
  {
    url: "/images/bfkmuaom_IMG_4740.png",
    alt: "Infield ground ball drill",
    caption: "Ground Ball Drills"
  },
  {
    url: "/images/he8jty0n_IMG_4733.jpeg",
    alt: "Group warmup exercises",
    caption: "Team Warmup"
  },
  {
    url: "/images/5h6vyq4t_IMG_4722.jpeg",
    alt: "Fielding practice on the diamond",
    caption: "Infield Practice"
  },
  {
    url: "/images/sbbhj663_IMG_4706.jpeg",
    alt: "Cone drill training",
    caption: "Agility Training"
  },
  {
    url: "/images/u4nwjo3y_IMG_4699.jpeg",
    alt: "Ground ball fielding practice",
    caption: "Fielding Fundamentals"
  }
];

const skillsData = [
  {
    icon: Target,
    titleKey: "hitting",
    descKey: "hittingDesc",
    image: "/images/z3olpt68_IMG_4769.jpeg"
  },
  {
    icon: Crosshair,
    titleKey: "pitching",
    descKey: "pitchingDesc",
    image: "/images/50jt1kn4_IMG_4761.jpeg"
  },
  {
    icon: Users,
    titleKey: "fielding",
    descKey: "fieldingDesc",
    image: "/images/64evyr35_IMG_4750.jpeg"
  },
  {
    icon: Trophy,
    titleKey: "gameIQ",
    descKey: "gameIQDesc",
    image: "/images/0vg929mq_IMG_4741.png"
  }
];

// FAQ Data - fallback if API fails
const defaultFaqs = [
  {
    question: "What age groups do you accept?",
    question_fr: "Quels groupes d'âge acceptez-vous?",
    answer: "We accept players from U9 to U15 (ages 9-15). Our programs are designed to accommodate different skill levels within each age group, from beginners to more experienced players.",
    answer_fr: "On accepte les joueurs de U9 à U15 (9-15 ans). Nos programmes sont conçus pour accommoder différents niveaux d'habileté dans chaque groupe d'âge, des débutants aux joueurs plus expérimentés."
  },
  {
    question: "What should my child bring to camp?",
    question_fr: "Que devrait apporter mon enfant au camp?",
    answer: "Players should bring their baseball glove, bat (if they have one), cleats or athletic shoes, a water bottle, and appropriate weather gear. We have some equipment available for those who need it.",
    answer_fr: "Les joueurs devraient apporter leur gant de baseball, bâton (s'ils en ont un), crampons ou chaussures de sport, une bouteille d'eau et des vêtements appropriés à la météo. On a de l'équipement disponible pour ceux qui en ont besoin."
  },
  {
    question: "Is prior baseball experience required?",
    question_fr: "Est-ce que l'expérience au baseball est requise?",
    answer: "No! We welcome players of all skill levels, including complete beginners. Our coaches tailor instruction to each player's current ability and help them progress at their own pace.",
    answer_fr: "Non! On accueille les joueurs de tous les niveaux, incluant les débutants complets. Nos entraîneurs adaptent l'instruction au niveau actuel de chaque joueur et les aident à progresser à leur propre rythme."
  },
  {
    question: "What happens if it rains?",
    question_fr: "Qu'est-ce qui se passe s'il pleut?",
    answer: "We monitor weather conditions closely. For light rain, we may continue with modified activities. For severe weather, sessions will be rescheduled. Parents will be notified via email/text of any cancellations.",
    answer_fr: "On surveille les conditions météo de proche. Pour une pluie légère, on peut continuer avec des activités modifiées. Pour le mauvais temps sévère, les sessions seront reportées. Les parents seront avisés par courriel/texto de toute annulation."
  },
  {
    question: "Can parents watch the sessions?",
    question_fr: "Est-ce que les parents peuvent regarder les sessions?",
    answer: "Absolutely! Parents are welcome to watch from designated viewing areas. We encourage family involvement and are happy to discuss your child's progress at any time.",
    answer_fr: "Absolument! Les parents sont les bienvenus pour regarder des zones de visionnement désignées. On encourage l'implication familiale et on est contents de discuter du progrès de votre enfant en tout temps."
  },
  {
    question: "How do I register and pay?",
    question_fr: "Comment est-ce que je m'inscris et je paie?",
    answer: "Registration is done through our website. Payment is accepted via e-transfer to burnselitebaseball@gmail.com. Full payment is required to secure your spot.",
    answer_fr: "L'inscription se fait par notre site web. Le paiement est accepté par virement Interac à burnselitebaseball@gmail.com. Le paiement complet est requis pour réserver ta place."
  },
  {
    question: "What is your refund policy?",
    question_fr: "Quelle est votre politique de remboursement?",
    answer: "Full refunds are available up to 14 days before camp starts. 50% refund for cancellations 7-14 days before. No refunds within 7 days of camp start, though credits may be applied to future sessions.",
    answer_fr: "Les remboursements complets sont disponibles jusqu'à 14 jours avant le début du camp. Remboursement de 50% pour les annulations 7-14 jours avant. Aucun remboursement dans les 7 jours du début, mais des crédits peuvent être appliqués aux sessions futures."
  },
  {
    question: "Is the camp for both boys and girls?",
    question_fr: "Est-ce que le camp est pour les garçons et les filles?",
    answer: "Yes! Burns Elite Baseball is open to both boys and girls. We welcome players from baseball and softball backgrounds.",
    answer_fr: "Oui! Burns Elite Baseball est ouvert aux garçons et aux filles. On accueille les joueurs de baseball et de softball."
  }
];

// Equipment checklist keys mapped to translation keys
const equipmentList = [
  { itemKey: "glove", noteKey: "gloveNote", required: true },
  { itemKey: "bat", noteKey: "batNote", required: false },
  { itemKey: "cleats", noteKey: "cleatsNote", required: true },
  { itemKey: "water", noteKey: "waterNote", required: true },
  { itemKey: "cap", noteKey: "capNote", required: false },
  { itemKey: "sunscreen", noteKey: "sunscreenNote", required: false },
  { itemKey: "clothes", noteKey: "clothesNote", required: true },
  { itemKey: "battingGloves", noteKey: "battingGlovesNote", required: false }
];

export default function LandingPage() {
  const { t, language } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [camps, setCamps] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [founder, setFounder] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState(defaultFaqs);
  const [isLoading, setIsLoading] = useState(true);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  
  // FAQ state
  const [openFaq, setOpenFaq] = useState(null);
  
  // Founder bio modal
  const [showFullBio, setShowFullBio] = useState(false);
  
  // Coach bio modal
  const [selectedCoach, setSelectedCoach] = useState(null);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  
  // Newsletter state
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubmittingNewsletter, setIsSubmittingNewsletter] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campsRes, coachesRes, testimonialsRes, faqsRes] = await Promise.all([
        axios.get(`${API}/camps`),
        axios.get(`${API}/coaches`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/faqs`)
      ]);
      setCamps(campsRes.data.slice(0, 3));
      setTestimonials(testimonialsRes.data);
      
      // Use fetched FAQs if available, otherwise keep defaults
      if (faqsRes.data && faqsRes.data.length > 0) {
        setFaqs(faqsRes.data);
      }
      
      // Separate founder from coaches
      const allCoaches = coachesRes.data;
      const founderData = allCoaches.find(c => c.is_founder);
      const otherCoaches = allCoaches.filter(c => !c.is_founder);
      setFounder(founderData);
      setCoaches(otherCoaches);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingContact(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent! We'll get back to you soon.");
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmittingContact(false);
    }
  };
  
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingNewsletter(true);
    try {
      // Get current language from localStorage
      const currentLanguage = localStorage.getItem('burns-elite-language') || 'en';
      
      await axios.post(`${API}/newsletter`, { 
        email: newsletterEmail,
        language: currentLanguage
      });
      toast.success("You're subscribed! Watch your inbox for updates.");
      setNewsletterEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("This email is already subscribed.");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setIsSubmittingNewsletter(false);
    }
  };
  
  const openLightbox = (photo) => {
    setLightboxPhoto(photo);
    setLightboxOpen(true);
  };

  return (
    <div className="min-h-screen bg-burns-black" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-glass" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-3 cursor-pointer" data-testid="logo-link">
              <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-14 w-auto" />
            </a>

            {/* Navigation */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <a href="#about" className="text-zinc-400 hover:text-white transition-colors font-medium tracking-wide text-sm" data-testid="nav-about">{t('nav.about')}</a>
              <a href="#skills" className="text-zinc-400 hover:text-white transition-colors font-medium tracking-wide text-sm" data-testid="nav-skills">{t('nav.skills')}</a>
              <a href="#camps" className="text-zinc-400 hover:text-white transition-colors font-medium tracking-wide text-sm" data-testid="nav-camps">{t('nav.camps')}</a>
              <a href="#coaches" className="text-zinc-400 hover:text-white transition-colors font-medium tracking-wide text-sm" data-testid="nav-coaches">{t('nav.coaches')}</a>
              <a href="#gallery" className="text-zinc-400 hover:text-white transition-colors font-medium tracking-wide text-sm" data-testid="nav-gallery">{t('nav.gallery')}</a>
              <a href="#contact" className="text-zinc-400 hover:text-white transition-colors font-medium tracking-wide text-sm" data-testid="nav-contact">{t('nav.contact')}</a>
              <Link to="/tshirt" className="text-zinc-400 hover:text-white transition-colors font-medium tracking-wide text-sm" data-testid="nav-tshirt">
                <Shirt className="inline-block w-4 h-4 mr-1" />
                {t('nav.merch')}
              </Link>
              <Link to="/parent/login" className="text-burns-gold hover:text-white transition-colors font-medium tracking-wide text-sm" data-testid="nav-parent-portal">
                {t('nav.parentPortal')}
              </Link>
              <Link to="/register" data-testid="nav-register-btn">
                <Button className="btn-primary px-4 lg:px-6 py-2 rounded-sm text-sm">
                  {t('nav.registerNow')}
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-burns-black border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              <a href="#about" className="block text-zinc-400 hover:text-white py-2" onClick={() => setIsMenuOpen(false)}>{t('nav.about')}</a>
              <a href="#skills" className="block text-zinc-400 hover:text-white py-2" onClick={() => setIsMenuOpen(false)}>{t('nav.skills')}</a>
              <a href="#camps" className="block text-zinc-400 hover:text-white py-2" onClick={() => setIsMenuOpen(false)}>{t('nav.camps')}</a>
              <a href="#coaches" className="block text-zinc-400 hover:text-white py-2" onClick={() => setIsMenuOpen(false)}>{t('nav.coaches')}</a>
              <a href="#gallery" className="block text-zinc-400 hover:text-white py-2" onClick={() => setIsMenuOpen(false)}>{t('nav.gallery')}</a>
              <a href="#contact" className="block text-zinc-400 hover:text-white py-2" onClick={() => setIsMenuOpen(false)}>{t('nav.contact')}</a>
              <Link to="/tshirt" className="block text-zinc-400 hover:text-white py-2" onClick={() => setIsMenuOpen(false)}>{t('nav.merch')}</Link>
              <Link to="/parent/login" className="block text-burns-gold hover:text-white py-2" onClick={() => setIsMenuOpen(false)}>{t('nav.parentPortal')}</Link>
              <Link to="/register" className="block" onClick={() => setIsMenuOpen(false)}>
                <Button className="btn-primary w-full rounded-sm">{t('nav.registerNow')}</Button>
              </Link>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-bg min-h-screen flex items-center justify-center relative" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-burns-black/50 to-burns-black"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img 
              src={LOGO_URL} 
              alt="Burns Elite Baseball Academy" 
              className="w-48 sm:w-64 md:w-80 mx-auto mb-8"
              data-testid="hero-logo"
            />
            <h1 className="font-heading text-5xl sm:text-6xl md:text-8xl font-black uppercase tracking-tighter text-white text-glow-red mb-6" data-testid="hero-title">
              {t('hero.title')} <span className="text-burns-red">{t('hero.titleAccent')}</span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 max-w-2xl mx-auto mb-4 font-body" data-testid="hero-subtitle">
              {t('hero.subtitle')}
            </p>
            <p className="text-lg text-burns-gold font-heading tracking-wide mb-10" data-testid="hero-tagline">
              {t('hero.services')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" data-testid="hero-register-btn">
                <Button className="btn-primary text-lg px-10 py-6 rounded-sm w-full sm:w-auto">
                  {t('hero.joinCamp')}
                  <ChevronRight className="ml-2" />
                </Button>
              </Link>
              <Link to="/calendar" data-testid="hero-calendar-btn">
                <Button className="btn-secondary text-lg px-10 py-6 rounded-sm w-full sm:w-auto">
                  {t('hero.viewCalendar')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="text-zinc-500"
          >
            <ChevronRight className="rotate-90 w-8 h-8" />
          </motion.div>
        </div>
      </section>

      {/* Founder Section */}
      {founder && (
        <section id="about" className="py-24 bg-zinc-950" data-testid="founder-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4" data-testid="founder-title">
                {t('founder.sectionTitle')} <span className="text-burns-gold">{t('founder.sectionTitleAccent')}</span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid md:grid-cols-2 gap-12 items-center"
            >
              <div className="relative">
                <div className="aspect-square rounded-sm overflow-hidden border-2 border-burns-gold/30 glow-gold">
                  <img 
                    src={getPhotoUrl(founder.photo_url)} 
                    alt={founder.name}
                    className="w-full h-full object-cover"
                    data-testid="founder-photo"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-burns-red px-6 py-3 rounded-sm">
                  <span className="font-heading text-white text-lg">{founder.experience_years}+ {t('founder.yearsLabel')}</span>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Award className="w-8 h-8 text-burns-gold" />
                  <span className="text-burns-gold font-heading text-lg tracking-wide">{t('founder.founderTitle')}</span>
                </div>
                <h3 className="font-heading text-4xl font-bold text-white mb-6" data-testid="founder-name">{founder.name}</h3>
                
                {/* Short bio blurb */}
                <p className="text-zinc-400 text-lg leading-relaxed mb-4" data-testid="founder-bio">
                  {(() => {
                    const bio = language === 'fr' && founder.bio_fr ? founder.bio_fr : founder.bio;
                    return bio.split('\n\n')[0];
                  })()}
                </p>
                
                <button
                  onClick={() => setShowFullBio(true)}
                  className="text-burns-gold hover:text-white transition-colors font-heading tracking-wide flex items-center gap-2 mb-6"
                  data-testid="read-more-btn"
                >
                  {t('founder.readFullProfile')}
                  <ChevronRight className="w-4 h-4" />
                </button>
                
                <div className="flex flex-wrap gap-2">
                  {(language === 'fr' && founder.specialties_fr?.length > 0 ? founder.specialties_fr : founder.specialties).map((specialty, idx) => {
                    // Map English specialties to translation keys
                    const specialtyMap = {
                      'Player Development': t('founder.playerDev'),
                      'Hitting': t('founder.hitting'),
                      'Team Strategy': t('founder.teamStrategy'),
                      'Mental Game': t('founder.mentalGame'),
                      'Pitching': t('founder.pitching'),
                      'Fielding': t('founder.fielding'),
                      'Base Running': t('founder.baseRunning')
                    };
                    return (
                      <span 
                        key={idx} 
                        className="bg-burns-dark border border-burns-gold/30 text-burns-gold px-4 py-2 rounded-sm text-sm font-medium"
                      >
                        {specialtyMap[specialty] || specialty}
                      </span>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Skills Section */}
      <section id="skills" className="py-24 bg-burns-black" data-testid="skills-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4" data-testid="skills-title">
              {t('skills.sectionTitle')} <span className="text-burns-gold">{t('skills.sectionTitleAccent')}</span>
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              {t('skills.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {skillsData.map((skill, index) => (
              <motion.div
                key={skill.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="skill-card bg-zinc-950 rounded-sm overflow-hidden group"
                data-testid={`skill-card-${skill.titleKey}`}
              >
                <div className="h-40 overflow-hidden">
                  <img 
                    src={skill.image} 
                    alt={t(`skills.${skill.titleKey}`)}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <skill.icon className="w-6 h-6 text-burns-red" />
                    <h3 className="font-heading text-xl font-bold text-white">{t(`skills.${skill.titleKey}`)}</h3>
                  </div>
                  <p className="text-zinc-400 text-sm leading-relaxed">{t(`skills.${skill.descKey}`)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Camps Section */}
      <section id="camps" className="py-24 bg-zinc-950" data-testid="camps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4" data-testid="camps-title">
              {t('camps.sectionTitle')} <span className="text-burns-red">{t('camps.sectionTitleAccent')}</span>
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              {t('camps.subtitle')}
            </p>
          </motion.div>

          {isLoading ? (
            <div className="text-center text-zinc-400">Loading camps...</div>
          ) : camps.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16 bg-burns-dark rounded-sm border border-white/10"
              data-testid="no-camps-message"
            >
              <Calendar className="w-16 h-16 text-burns-gold mx-auto mb-4" />
              <h3 className="font-heading text-2xl text-white mb-2">NEW CAMPS COMING SOON</h3>
              <p className="text-zinc-400 mb-6">Check back for upcoming training sessions</p>
              <Link to="/register">
                <Button className="btn-secondary">GET NOTIFIED</Button>
              </Link>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {camps.map((camp, index) => (
                <motion.div
                  key={camp.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-burns-dark border border-white/10 rounded-sm p-6 hover:border-burns-gold transition-colors"
                  data-testid={`camp-card-${camp.id}`}
                >
                  <div className="flex items-center gap-2 text-burns-gold text-sm font-heading tracking-wide mb-3">
                    <Calendar className="w-4 h-4" />
                    {camp.start_date} - {camp.end_date}
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white mb-2">
                    {language === 'fr' && camp.name_fr ? camp.name_fr : camp.name}
                  </h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    {language === 'fr' && camp.description_fr ? camp.description_fr : camp.description}
                  </p>
                  <div className="flex items-center justify-between text-sm text-zinc-500 mb-4">
                    <span>{camp.age_group}</span>
                    <span>{camp.time}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400 text-sm mb-4">
                    <MapPin className="w-4 h-4" />
                    {camp.location}
                  </div>
                  <Link to={`/register?camp=${camp.id}`}>
                    <Button className="btn-primary w-full rounded-sm">{t('camps.register')}</Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/register" data-testid="view-all-camps-btn">
              <Button className="btn-secondary px-8 py-4 rounded-sm">
                {t('camps.viewAll')}
                <ChevronRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Coaches Section */}
      {coaches.length > 0 && (
        <section id="coaches" className="py-24 bg-burns-black" data-testid="coaches-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4" data-testid="coaches-title">
                {t('coaches.sectionTitle')} <span className="text-burns-red">{t('coaches.sectionTitleAccent')}</span>
              </h2>
              <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
                {t('coaches.subtitle')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coaches.map((coach, index) => (
                <motion.div
                  key={coach.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-zinc-950 border border-white/10 rounded-sm overflow-hidden group hover:border-burns-red transition-colors"
                  data-testid={`coach-card-${coach.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={getPhotoUrl(coach.photo_url)} 
                      alt={coach.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-burns-gold text-sm font-heading tracking-wide mb-2">
                      <Star className="w-4 h-4" />
                      {coach.experience_years} {t('coaches.yearsExperience')}
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-white mb-1">{coach.name}</h3>
                    <p className="text-burns-red font-medium mb-3">
                      {(() => {
                        const titleMap = {
                          'Founder & Head Coach': t('founder.founderTitle'),
                          'Pitching Coach': t('coaches.pitchingCoach'),
                          'Hitting Coach': t('coaches.hittingCoach'),
                          'Fielding Coach': t('coaches.fieldingCoach'),
                          'Assistant Coach': t('coaches.assistantCoach'),
                          'Head Coach': t('coaches.headCoach'),
                          'Hitting & Outfield Coach': t('coaches.hittingOutfieldCoach'),
                          'Infield & Catching Coach': t('coaches.infieldCatchingCoach'),
                          'Outfield Coach': t('coaches.outfieldCoach'),
                          'Catching Coach': t('coaches.catchingCoach'),
                          'Infield Coach': t('coaches.infieldCoach')
                        };
                        return titleMap[coach.title] || (language === 'fr' && coach.title_fr ? coach.title_fr : coach.title);
                      })()}
                    </p>
                    <p className="text-zinc-400 text-sm leading-relaxed mb-4 line-clamp-3">
                      {language === 'fr' && coach.bio_fr ? coach.bio_fr : coach.bio}
                    </p>
                    <button
                      onClick={() => setSelectedCoach(coach)}
                      className="text-burns-gold hover:text-white transition-colors font-heading tracking-wide text-sm flex items-center gap-1 mb-4"
                      data-testid={`coach-read-more-${coach.id}`}
                    >
                      {t('founder.learnMore')}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <div className="flex flex-wrap gap-2">
                      {(language === 'fr' && coach.specialties_fr?.length > 0 ? coach.specialties_fr : coach.specialties).slice(0, 3).map((specialty, idx) => {
                        const specialtyMap = {
                          'Player Development': t('founder.playerDev'),
                          'Hitting': t('founder.hitting'),
                          'Team Strategy': t('founder.teamStrategy'),
                          'Mental Game': t('founder.mentalGame'),
                          'Pitching': t('founder.pitching'),
                          'Fielding': t('founder.fielding'),
                          'Base Running': t('founder.baseRunning'),
                          'Youth Coaching': t('coaches.youthCoaching'),
                          'Arm Care': t('coaches.armCare'),
                          'Strength Training': t('coaches.strengthTraining'),
                          'Conditioning': t('coaches.conditioning'),
                          'Pitching Mechanics': t('coaches.pitchingMechanics'),
                          'Hitting Mechanics': t('coaches.hittingMechanics'),
                          'Outfield Defense': t('coaches.outfieldDefense'),
                          'Infield Defense': t('coaches.infieldDefense'),
                          'Catching': t('coaches.catching'),
                          'Game Strategy': t('coaches.gameStrategy')
                        };
                        return (
                          <span 
                            key={idx} 
                            className="bg-burns-dark text-zinc-400 px-3 py-1 rounded-sm text-xs"
                          >
                            {specialtyMap[specialty] || specialty}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Photo Gallery Section */}
      <section id="gallery" className="py-24 bg-zinc-950" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4" data-testid="gallery-title">
              {t('gallery.moments')}
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              {t('gallery.momentsSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
            {CAMP_PHOTOS.map((photo, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`relative overflow-hidden rounded-sm group cursor-pointer ${
                  index === 0 ? 'col-span-2 row-span-2' : ''
                }`}
                onClick={() => openLightbox(photo)}
                data-testid={`gallery-photo-${index}`}
              >
                <div className={`${index === 0 ? 'aspect-square' : 'aspect-square'} overflow-hidden`}>
                  <img 
                    src={photo.url} 
                    alt={photo.alt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-2 md:p-4">
                    <p className="text-white font-heading text-xs md:text-base">{photo.caption}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* T-Shirt Section */}
      <section className="py-24 bg-burns-black border-y border-white/10" data-testid="tshirt-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Shirt className="w-16 h-16 text-burns-gold mb-6" />
              <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4" data-testid="tshirt-title">
                {t('merch.gearUp')} <span className="text-burns-red">{t('merch.gearUpAccent')}</span>
              </h2>
              <p className="text-zinc-400 text-lg mb-6">
                {t('merch.description')}
              </p>
              <Link to="/tshirt" data-testid="preorder-btn">
                <Button className="btn-primary px-8 py-4 rounded-sm">
                  {t('merch.preorderNow')}
                  <ChevronRight className="ml-2" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square bg-zinc-950 rounded-sm border border-white/10 flex items-center justify-center glow-gold">
                <img 
                  src={LOGO_URL} 
                  alt="Burns Elite Baseball Logo" 
                  className="w-3/4 h-3/4 object-contain"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-zinc-950" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4" data-testid="contact-title">
              {t('contact.sectionTitle')} <span className="text-burns-gold">{t('contact.sectionTitleAccent')}</span>
            </h2>
            <p className="text-zinc-400 text-lg">{t('contact.subtitle')}</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-burns-dark border border-white/10 rounded-sm p-8"
            >
              <h3 className="font-heading text-2xl font-bold text-white mb-6">{t('contact.sendMessage')}</h3>
              <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <Input
                    placeholder={`${t('contact.namePlaceholder')} *`}
                    value={contactForm.name}
                    onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-transparent border-white/20 text-white placeholder:text-zinc-500"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    type="email"
                    placeholder={`${t('contact.emailPlaceholder')} *`}
                    value={contactForm.email}
                    onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-transparent border-white/20 text-white placeholder:text-zinc-500"
                    required
                    data-testid="contact-email-input"
                  />
                  <Input
                    type="tel"
                    placeholder={t('contact.phonePlaceholder')}
                    value={contactForm.phone}
                    onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-transparent border-white/20 text-white placeholder:text-zinc-500"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder={`${t('contact.messagePlaceholder')} *`}
                    value={contactForm.message}
                    onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                    className="bg-transparent border-white/20 text-white placeholder:text-zinc-500 min-h-[120px]"
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="btn-primary w-full rounded-sm"
                  disabled={isSubmittingContact}
                  data-testid="contact-submit-btn"
                >
                  {isSubmittingContact ? t('contact.sending') : t('contact.send')}
                  <Send className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </motion.div>

            {/* Contact Info + Map */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-burns-dark border border-white/10 rounded-sm p-6">
                  <div className="w-12 h-12 bg-burns-red/10 rounded-full flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-burns-red" />
                  </div>
                  <h4 className="font-heading text-lg font-bold text-white mb-1">{t('contact.location')}</h4>
                  <p className="text-zinc-400 text-sm">Miramichi, New Brunswick</p>
                </div>
                <div className="bg-burns-dark border border-white/10 rounded-sm p-6">
                  <div className="w-12 h-12 bg-burns-gold/10 rounded-full flex items-center justify-center mb-4">
                    <Mail className="w-6 h-6 text-burns-gold" />
                  </div>
                  <h4 className="font-heading text-lg font-bold text-white mb-1">{t('contact.emailLabel')}</h4>
                  <a href="mailto:Burnselitebaseball@gmail.com" className="text-zinc-400 text-sm hover:text-burns-gold transition-colors break-all">
                    Burnselitebaseball@gmail.com
                  </a>
                </div>
              </div>

              {/* Google Map */}
              <div className="bg-burns-dark border border-white/10 rounded-sm overflow-hidden" data-testid="google-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d44883.37247742893!2d-65.52084565!3d47.0289735!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4ca3b1b5a3d3f1c9%3A0x1d5f2d0b8f3c5a0a!2sMiramichi%2C%20NB!5e0!3m2!1sen!2sca!4v1709900000000!5m2!1sen!2sca"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Burns Elite Baseball Location"
                ></iframe>
              </div>

              {/* Social Links */}
              <div className="bg-burns-dark border border-white/10 rounded-sm p-6">
                <h4 className="font-heading text-lg font-bold text-white mb-4">{t('contact.followUs')}</h4>
                <div className="flex items-center gap-4">
                  <a href="https://www.facebook.com/share/1BRr3BXxPm/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-400 hover:text-burns-gold transition-colors" data-testid="social-facebook">
                    <Facebook className="w-6 h-6" />
                    <span className="text-sm">Facebook</span>
                  </a>
                  <a href="https://www.instagram.com/burnselitebaseball?igsh=MWx4aWlvOTVlYmRkcQ==" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-400 hover:text-burns-gold transition-colors" data-testid="social-instagram">
                    <Instagram className="w-6 h-6" />
                    <span className="text-sm">Instagram</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section id="testimonials" className="py-24 bg-burns-black" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4">
                {t('testimonials.sectionTitle')} <span className="text-burns-gold">{t('testimonials.sectionTitleAccent')}</span>
              </h2>
              <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
                {t('testimonials.subtitle')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-zinc-950 border border-white/10 rounded-sm p-6 relative"
                  data-testid={`testimonial-${testimonial.id}`}
                >
                  <Quote className="w-8 h-8 text-burns-gold/30 absolute top-4 right-4" />
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-burns-gold fill-burns-gold" />
                    ))}
                  </div>
                  <p className="text-zinc-300 text-sm mb-4 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="text-white font-heading font-bold">{testimonial.name}</p>
                    <p className="text-burns-gold text-xs">{testimonial.role}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Equipment Checklist Section */}
      <section id="equipment" className="py-24 bg-zinc-950" data-testid="equipment-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Backpack className="w-12 h-12 text-burns-gold mb-4" />
              <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4">
                {t('equipment.heading')} <span className="text-burns-red">{t('equipment.headingAccent')}</span>
              </h2>
              <p className="text-zinc-400 text-lg mb-6">
                {t('equipment.description')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-burns-dark border border-white/10 rounded-sm p-6"
            >
              <ul className="space-y-3">
                {equipmentList.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className={`w-5 h-5 mt-0.5 ${item.required ? 'text-burns-red' : 'text-burns-gold'}`} />
                    <div>
                      <span className="text-white font-medium">{t(`equipment.items.${item.itemKey}`)}</span>
                      {item.required && <span className="text-burns-red text-xs ml-2">{t('equipment.required')}</span>}
                      <p className="text-zinc-500 text-sm">{t(`equipment.items.${item.noteKey}`)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-burns-black" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <HelpCircle className="w-12 h-12 text-burns-gold mx-auto mb-4" />
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4">
              {t('faq.sectionTitle')} <span className="text-burns-red">{t('faq.sectionTitleAccent')}</span>
            </h2>
          </motion.div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-zinc-950 border border-white/10 rounded-sm overflow-hidden"
                data-testid={`faq-${index}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="text-white font-medium pr-4">
                    {language === 'fr' && faq.question_fr ? faq.question_fr : faq.question}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-burns-gold transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="px-4 pb-4 text-zinc-400 text-sm border-t border-white/10 pt-4">
                        {language === 'fr' && faq.answer_fr ? faq.answer_fr : faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Policies Section */}
      <section id="policies" className="py-24 bg-zinc-950" data-testid="policies-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4">
              {t('policies.sectionTitle')} <span className="text-burns-gold">{t('policies.sectionTitleAccent')}</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-burns-dark border border-white/10 rounded-sm p-6"
            >
              <div className="w-12 h-12 bg-burns-red/10 rounded-full flex items-center justify-center mb-4">
                <CloudRain className="w-6 h-6 text-burns-red" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">{t('policies.weather')}</h3>
              <p className="text-zinc-400 text-sm">
                {t('policies.weatherDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-burns-dark border border-white/10 rounded-sm p-6"
            >
              <div className="w-12 h-12 bg-burns-gold/10 rounded-full flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-burns-gold" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">{t('policies.refund')}</h3>
              <p className="text-zinc-400 text-sm">
                {t('policies.refundDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-burns-dark border border-white/10 rounded-sm p-6"
            >
              <div className="w-12 h-12 bg-burns-red/10 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-burns-red" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">{t('policies.safety')}</h3>
              <p className="text-zinc-400 text-sm">
                {t('policies.safetyDesc')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-burns-red" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Mail className="w-12 h-12 text-white mx-auto mb-4" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
              {t('newsletter.title')}
            </h2>
            <p className="text-white/80 mb-6">
              {t('newsletter.subtitle')}
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder={t('newsletter.placeholder')}
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="bg-white/10 border-white/30 text-white placeholder:text-white/60 flex-1"
                required
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit" 
                className="bg-burns-black hover:bg-zinc-800 text-white px-6 rounded-sm"
                disabled={isSubmittingNewsletter}
                data-testid="newsletter-submit-btn"
              >
                {isSubmittingNewsletter ? "..." : t('newsletter.subscribe')}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-burns-black border-t border-white/10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-10 w-auto" />
              <span className="text-zinc-500 text-sm">© 2026 Burns Elite Baseball Academy</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <span>Owner: Tom Burns</span>
              <Link to="/admin" className="hover:text-burns-gold transition-colors" data-testid="admin-link">Admin</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Coach Bio Modal */}
      <AnimatePresence>
        {selectedCoach && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 overflow-y-auto"
            onClick={() => setSelectedCoach(null)}
            data-testid="coach-bio-modal"
          >
            <button
              onClick={() => setSelectedCoach(null)}
              className="fixed top-4 right-4 text-white hover:text-burns-gold z-[60] bg-black/50 rounded-full p-2"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="min-h-full flex items-start justify-center p-4 py-16">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="max-w-3xl w-full bg-zinc-950 border border-white/10 rounded-sm p-4 sm:p-6 md:p-8"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-6">
                  <div className="w-32 h-32 sm:w-48 sm:h-48 flex-shrink-0 mx-auto sm:mx-0">
                    <img 
                      src={getPhotoUrl(selectedCoach.photo_url)} 
                      alt={selectedCoach.name}
                      className="w-full h-full object-cover rounded-sm border-2 border-burns-red/30"
                    />
                  </div>
                  <div className="text-center sm:text-left">
                    <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                      <Star className="w-5 h-5 text-burns-gold" />
                      <span className="text-burns-gold font-heading tracking-wide text-sm">
                        {selectedCoach.experience_years} {t('coaches.yearsExperience')}
                      </span>
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">{selectedCoach.name}</h2>
                    <p className="text-burns-red font-medium mb-3">
                      {(() => {
                        const titleMap = {
                          'Pitching Coach': t('coaches.pitchingCoach'),
                          'Hitting Coach': t('coaches.hittingCoach'),
                          'Fielding Coach': t('coaches.fieldingCoach'),
                          'Assistant Coach': t('coaches.assistantCoach'),
                          'Head Coach': t('coaches.headCoach'),
                          'Hitting & Outfield Coach': t('coaches.hittingOutfieldCoach'),
                          'Infield & Catching Coach': t('coaches.infieldCatchingCoach'),
                          'Outfield Coach': t('coaches.outfieldCoach'),
                          'Catching Coach': t('coaches.catchingCoach'),
                          'Infield Coach': t('coaches.infieldCoach')
                        };
                        return titleMap[selectedCoach.title] || (language === 'fr' && selectedCoach.title_fr ? selectedCoach.title_fr : selectedCoach.title);
                      })()}
                    </p>
                    <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                      {(language === 'fr' && selectedCoach.specialties_fr?.length > 0 ? selectedCoach.specialties_fr : selectedCoach.specialties).map((specialty, idx) => {
                        const specialtyMap = {
                          'Player Development': t('founder.playerDev'),
                          'Hitting': t('founder.hitting'),
                          'Team Strategy': t('founder.teamStrategy'),
                          'Mental Game': t('founder.mentalGame'),
                          'Pitching': t('founder.pitching'),
                          'Fielding': t('founder.fielding'),
                          'Base Running': t('founder.baseRunning'),
                          'Youth Coaching': t('coaches.youthCoaching'),
                          'Arm Care': t('coaches.armCare'),
                          'Strength Training': t('coaches.strengthTraining'),
                          'Conditioning': t('coaches.conditioning'),
                          'Pitching Mechanics': t('coaches.pitchingMechanics'),
                          'Hitting Mechanics': t('coaches.hittingMechanics'),
                          'Outfield Defense': t('coaches.outfieldDefense'),
                          'Infield Defense': t('coaches.infieldDefense'),
                          'Catching': t('coaches.catching'),
                          'Game Strategy': t('coaches.gameStrategy')
                        };
                        return (
                          <span key={idx} className="bg-burns-dark border border-burns-red/30 text-zinc-300 px-2 py-1 rounded-sm text-xs">
                            {specialtyMap[specialty] || specialty}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="prose prose-invert max-w-none">
                  {(() => {
                    const bio = language === 'fr' && selectedCoach.bio_fr ? selectedCoach.bio_fr : selectedCoach.bio;
                    return bio.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="text-zinc-300 text-sm sm:text-base leading-relaxed mb-4 last:mb-0">
                        {paragraph}
                      </p>
                    ));
                  })()}
                </div>
                <div className="mt-6 pt-4 border-t border-white/10 flex justify-center">
                  <Button onClick={() => setSelectedCoach(null)} className="btn-primary rounded-sm">
                    CLOSE
                  </Button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Photo Lightbox */}
      <AnimatePresence>
        {lightboxOpen && lightboxPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setLightboxOpen(false)}
            data-testid="lightbox"
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 text-white hover:text-burns-gold z-10"
            >
              <X className="w-8 h-8" />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-5xl max-h-[90vh] relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightboxPhoto.url}
                alt={lightboxPhoto.alt}
                className="max-w-full max-h-[85vh] object-contain rounded-sm"
              />
              <p className="text-white text-center mt-4 font-heading text-xl">{lightboxPhoto.caption}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Founder Full Bio Modal */}
      <AnimatePresence>
        {showFullBio && founder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 overflow-y-auto"
            onClick={() => setShowFullBio(false)}
            data-testid="bio-modal"
          >
            {/* Close button - fixed position */}
            <button
              onClick={() => setShowFullBio(false)}
              className="fixed top-4 right-4 text-white hover:text-burns-gold z-[60] bg-black/50 rounded-full p-2"
              data-testid="bio-modal-close"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Scrollable content wrapper */}
            <div className="min-h-full flex items-start justify-center p-4 py-16">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="max-w-4xl w-full bg-zinc-950 border border-white/10 rounded-sm p-4 sm:p-6 md:p-8"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-6 sm:mb-8">
                  <div className="w-32 h-32 sm:w-48 sm:h-48 flex-shrink-0 mx-auto sm:mx-0">
                    <img 
                      src={getPhotoUrl(founder.photo_url)} 
                      alt={founder.name}
                      className="w-full h-full object-cover rounded-sm border-2 border-burns-gold/30"
                    />
                  </div>
                  <div className="text-center sm:text-left">
                    <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                      <Award className="w-5 h-5 sm:w-6 sm:h-6 text-burns-gold" />
                      <span className="text-burns-gold font-heading tracking-wide text-sm sm:text-base">
                        {language === 'fr' && founder.title_fr ? founder.title_fr : founder.title}
                      </span>
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">{founder.name}</h2>
                    <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                      {(language === 'fr' && founder.specialties_fr?.length > 0 ? founder.specialties_fr : founder.specialties).map((specialty, idx) => {
                        const specialtyMap = {
                          'Player Development': t('founder.playerDev'),
                          'Hitting': t('founder.hitting'),
                          'Team Strategy': t('founder.teamStrategy'),
                          'Mental Game': t('founder.mentalGame'),
                          'Pitching': t('founder.pitching'),
                          'Fielding': t('founder.fielding'),
                          'Base Running': t('founder.baseRunning')
                        };
                        return (
                          <span 
                            key={idx} 
                            className="bg-burns-dark border border-burns-gold/30 text-burns-gold px-2 sm:px-3 py-1 rounded-sm text-xs sm:text-sm"
                          >
                            {specialtyMap[specialty] || specialty}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
                
                <div className="prose prose-invert max-w-none">
                  {(() => {
                    const bio = language === 'fr' && founder.bio_fr ? founder.bio_fr : founder.bio;
                    return bio.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="text-zinc-300 text-sm sm:text-base leading-relaxed mb-4 last:mb-0">
                        {paragraph}
                      </p>
                    ));
                  })()}
                </div>
                
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-white/10 flex justify-center">
                  <Button 
                    onClick={() => setShowFullBio(false)}
                    className="btn-primary rounded-sm"
                  >
                    CLOSE
                  </Button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
